/**
 * W1 supplies map position/stage. The shared private reader supplies only the current journey's detail.
 * Current user/household/pet/journey determine its exact key, shared by Journey and both food consumers.
 * Conflicting facts or failed reads stop detail projection. No W1/focus/remount/reconnect/timer retry
 * after failure; retryable network/timeout permits one explicit deduplicated read.
 * W1 current leg is not its main leg: detail still uses snapshot.current_leg_id.
 */
import type { JourneyMapSnapshot, MapActivityEntry } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { useJourneyReader } from "@/shared/query/journeyReader";
import { useMapLeaving } from "./leaving";
import type { WorldPet } from "./model";
export { JOURNEY_STALE_MS, JOURNEY_FALLBACK_MS } from "@/shared/query/journeyReader";
import { JOURNEY_FALLBACK_MS, type JourneyDetails } from "@/shared/query/journeyReader";
export type { JourneyDetails } from "@/shared/query/journeyReader";

export function isAligned(snapshot: JourneyMapSnapshot | undefined, journeyId: string | null): snapshot is JourneyMapSnapshot {
  return Boolean(snapshot && journeyId && snapshot.journey_id === journeyId && snapshot.lifecycle === "active");
}

/** 保底刷新：只在对齐成功时开；还在读、读不到、对不上都不开。 */
export function journeyRefetchInterval(snapshot: JourneyMapSnapshot | undefined, journeyId: string | null): number | false {
  return isAligned(snapshot, journeyId) ? JOURNEY_FALLBACK_MS : false;
}

/** W1 这只的四样：(journey_id, phase, version, until)，一变就让快照失效。 */
export function worldJourneyKey(pet: WorldPet): string {
  return [pet.activity.journeyId ?? "-", pet.activity.phase, pet.version, pet.activity.until ?? "-"].join("|");
}

/** 当前这一段上还没结束、带媒体会话的车上活动（一起听 / 一起看）。 */
export function currentMediaEntry(snapshot: JourneyMapSnapshot): (MapActivityEntry & { media_session_id: string }) | null {
  const entry = snapshot.activity_entries.find((e) => e.leg_id === snapshot.current_leg_id && e.badge_state !== "ended" && Boolean(e.media_session_id));
  return entry ? (entry as MapActivityEntry & { media_session_id: string }) : null;
}

export function useJourneyDetails(pet: WorldPet | null): JourneyDetails {
  const leaving = useMapLeaving();
  return useJourneyReader({
    world: pet ? { petId: pet.petId, journeyId: pet.activity.journeyId, changeKey: worldJourneyKey(pet) } : null,
    enabled: env.dataMode === "live" && Boolean(pet), leaving,
  }).details;
}
